/**
 * KRI Entity - RTK Query API
 * API endpoints for Key Risk Indicators using RTK Query with Django REST Framework
 *
 * Note: KRIs are now part of the Risk API (/api/v1/risks/kris/)
 * This file re-exports from riskApi for backward compatibility
 */

// Create a standalone KRI API for components that import from here
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';

// rag_status comes back lowercase ('green'/'amber'/'red'/'unknown'); the UI's
// badge/icon helpers switch on the title-cased label below.
const RAG_TO_LABEL = { green: 'Green', amber: 'Yellow', red: 'Red' };

// Maps the Django KRI serializer's field names to the names the KRI
// dashboard/card/detail UI expects (this entity predates, and was never
// reconciled with, the actual backend schema).
const normalizeKri = (kri) => ({
  ...kri,
  kri_name: kri.name,
  kri_code: '', // backend has no KRI code field
  kri_category: kri.risk_title || 'Uncategorized',
  measurement_unit: kri.unit || '',
  latest_measured_value:
    kri.current_value !== null && kri.current_value !== undefined
      ? Number(kri.current_value)
      : null,
  latest_threshold_status: RAG_TO_LABEL[kri.rag_status] || null,
  last_measurement_date: kri.last_updated,
  measurement_frequency: kri.collection_frequency,
  data_source_type: kri.data_source,
  linked_risk: kri.risk_title || null,
  inverse_threshold: kri.direction === 'lower_is_worse',
  green_max: kri.threshold_green,
  yellow_max: kri.threshold_amber,
  red_max: kri.threshold_red,
});

// Maps a Django KRIReading to the measurement shape MeasurementsTab/TrendsTab expect.
const normalizeReading = (reading) => ({
  ...reading,
  measurement_date: reading.reading_date,
  measured_value:
    reading.value !== null && reading.value !== undefined ? Number(reading.value) : null,
  recorded_by: reading.recorded_by_name || reading.recorded_by,
});

export const kriApi = createApi({
  reducerPath: 'kriApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['KRI', 'KRIReading'],
  endpoints: (builder) => ({
    // =========================================================================
    // KRIs
    // =========================================================================
    getKRIs: builder.query({
      query: ({ filters = {}, page = 1, limit = 20, ordering = '-created_at' } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        params.set('ordering', ordering);

        if (filters.status && filters.status !== 'all') {
          params.set('status', filters.status);
        }
        if (filters.risk) {
          // RSK-25: KRIViewSet.get_queryset (apps/risk/views.py) filters on
          // `risk_id`, not `risk` -- the unrecognised param was a silent no-op.
          params.set('risk_id', filters.risk);
        }
        if (filters.search) {
          params.set('search', filters.search);
        }

        return {
          url: `/api/v1/risks/kris/?${params.toString()}`,
        };
      },
      transformResponse: (response) => {
        const list = response.results || response || [];
        return {
          data: list.map(normalizeKri),
          total: response.count || list.length || 0,
        };
      },
      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ id }) => ({ type: 'KRI', id })), { type: 'KRI', id: 'LIST' }]
          : [{ type: 'KRI', id: 'LIST' }],
    }),

    getKRIById: builder.query({
      query: (id) => ({
        url: `/api/v1/risks/kris/${id}/`,
      }),
      transformResponse: (response) => normalizeKri(response),
      providesTags: (result, error, id) => [{ type: 'KRI', id }],
    }),

    createKRI: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/kris/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'KRI', id: 'LIST' }],
    }),

    updateKRI: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/kris/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'KRI', id },
        { type: 'KRI', id: 'LIST' },
      ],
    }),

    deleteKRI: builder.mutation({
      query: (id) => ({
        url: `/api/v1/risks/kris/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'KRI', id: 'LIST' }],
    }),

    // =========================================================================
    // KRI Readings
    // =========================================================================
    getKRIReadings: builder.query({
      query: ({ kriId, page = 1, limit = 50 } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        if (kriId) {
          // RSK-24: KRIReadingViewSet.get_queryset (apps/risk/views.py) filters
          // on `kri_id`, not `kri` -- an unrecognised param is a silent no-op in
          // DRF, so this used to return every tenant KRI reading unfiltered.
          params.set('kri_id', kriId);
        }

        return {
          url: `/api/v1/risks/kri-readings/?${params.toString()}`,
        };
      },
      transformResponse: (response) => {
        const list = response.results || response || [];
        return {
          data: list.map(normalizeReading),
          total: response.count || list.length || 0,
        };
      },
      providesTags: ['KRIReading'],
    }),

    createKRIReading: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/kri-readings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['KRIReading', { type: 'KRI', id: 'LIST' }],
    }),

    // RSK-23: unlike createKRIReading above (dead code -- grepped, never
    // consumed by any page/component), this hits KRIViewSet.record_reading
    // (apps/risk/views.py), the detail action that ALSO updates
    // kri.current_value/last_updated (and therefore rag_status) after
    // creating the reading. The generic ModelViewSet path above does not
    // touch those derived fields at all, so it would silently leave a KRI's
    // displayed current value/threshold status stale after a new reading.
    // record_reading reads kri from the URL pk, and expects
    // reading_date/value/notes in the body (not KRIReadingSerializer's
    // field names verbatim) -- see the view for the exact request.data.get
    // calls.
    recordKRIReading: builder.mutation({
      query: ({ kriId, ...data }) => ({
        url: `/api/v1/risks/kris/${kriId}/record_reading/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { kriId }) => [
        { type: 'KRI', id: kriId },
        { type: 'KRI', id: 'LIST' },
        'KRIReading',
      ],
    }),

    // =========================================================================
    // Statistics
    // =========================================================================
    getKRISummary: builder.query({
      query: () => ({
        url: '/api/v1/risks/kris/summary/',
      }),
    }),

    // There's no dedicated /trends/ endpoint on the backend — reuse the
    // existing /readings/ action (history) as the chart's fallback series.
    getKRITrends: builder.query({
      query: ({ kriId } = {}) => ({
        url: `/api/v1/risks/kris/${kriId}/readings/`,
      }),
      transformResponse: (response) => {
        const list = Array.isArray(response) ? response : response.results || [];
        return list.map((reading) => ({
          date: reading.reading_date,
          predictedValue:
            reading.value !== null && reading.value !== undefined ? Number(reading.value) : null,
        }));
      },
    }),

    getKRIForecast: builder.query({
      query: ({ kriId } = {}) => ({
        url: `/api/v1/risks/kris/${kriId}/forecast/`,
      }),
      // forecast data: { forecast, lower_bound, upper_bound, model_version, fallback, mape }
    }),

    getKRIAnomalyScore: builder.query({
      query: ({ kriId } = {}) => ({
        url: `/api/v1/risks/kris/${kriId}/anomaly/`,
      }),
      // anomaly data: { score, anomaly_type, model_version }
    }),
  }),
});

export const {
  useGetKRIsQuery: useGetKRIsQueryLocal,
  useGetKRIByIdQuery: useGetKRIByIdQueryLocal,
  useCreateKRIMutation: useCreateKRIMutationLocal,
  useUpdateKRIMutation: useUpdateKRIMutationLocal,
  useDeleteKRIMutation: useDeleteKRIMutationLocal,
  useGetKRIReadingsQuery: useGetKRIReadingsQueryLocal,
  useCreateKRIReadingMutation: useCreateKRIReadingMutationLocal,
  useRecordKRIReadingMutation: useRecordKRIReadingMutationLocal,
  useGetKRISummaryQuery,
  useGetKRITrendsQuery,
  useGetKRIForecastQuery,
  useGetKRIAnomalyScoreQuery,
} = kriApi;

// Re-export with original names (use the locally-generated hooks, not the undefined riskApi imports)
export {
  useGetKRIsQueryLocal as useGetKRIsQuery,
  useGetKRIByIdQueryLocal as useGetKRIByIdQuery,
  useCreateKRIMutationLocal as useCreateKRIMutation,
  useUpdateKRIMutationLocal as useUpdateKRIMutation,
  useDeleteKRIMutationLocal as useDeleteKRIMutation,
  useGetKRIReadingsQueryLocal as useGetKRIReadingsQuery,
  useCreateKRIReadingMutationLocal as useCreateKRIReadingMutation,
  useRecordKRIReadingMutationLocal as useRecordKRIReadingMutation,
};

export default kriApi;
